<template>
  <div class="pg-line">
    <div class="p-form-grid p-form-grid--2">
      <div class="p-field">
        <label class="p-field__label">Section Title</label>
        <input
          v-model="sectionData.content.sectionTitle"
          type="text"
          class="p-input"
          placeholder="섹션 제목을 입력하세요"
        />
      </div>
      <div class="p-field">
        <label class="p-field__label">Line Mode (연결 방식)</label>
        <select v-model="sectionData.content.lineMode" class="p-input">
          <option value="plain">Plain — 텍스트/이미지 연결</option>
          <option value="audio">Audio — 음성 듣고 연결</option>
        </select>
      </div>
    </div>

    <div class="pg-line__list">
      <div
        v-for="(line, index) in sectionData.content.data"
        :key="index"
        class="pg-line__item"
      >
        <div class="pg-line__item-head">
          <span class="pg-line__index">{{ index + 1 }}</span>
          <div class="pg-line__item-actions">
            <button
              type="button"
              class="pg-line__icon-btn"
              title="위로 이동"
              :disabled="index === 0"
              @click.prevent="moveUp(index)"
            >
              <i class="material-icons-round">arrow_upward</i>
            </button>
            <button
              type="button"
              class="pg-line__icon-btn"
              title="아래로 이동"
              :disabled="index === sectionData.content.data.length - 1"
              @click.prevent="moveDown(index)"
            >
              <i class="material-icons-round">arrow_downward</i>
            </button>
            <button
              type="button"
              class="pg-line__icon-btn pg-line__icon-btn--danger"
              title="제거"
              @click.prevent="removeItem(index)"
            >
              <i class="material-icons-round">delete</i>
            </button>
          </div>
        </div>

        <div class="pg-line__item-body">
          <div class="pg-line__fields">
            <div class="p-field">
              <label class="p-field__label">Left Text</label>
              <input
                v-model="line.left"
                type="text"
                class="p-input"
                placeholder="왼쪽 텍스트"
              />
            </div>
            <div class="p-field">
              <label class="p-field__label">Right Text</label>
              <input
                v-model="line.right"
                type="text"
                class="p-input"
                placeholder="오른쪽 텍스트"
              />
            </div>
            <div v-if="sectionData.content.lineMode === 'audio'" class="p-field">
              <label class="p-field__label">
                <i class="material-icons-round pg-line__audio-ico">music_note</i>
                Audio URL (행별 음성)
              </label>
              <input
                v-model="line.audioUrl"
                type="text"
                class="p-input"
                placeholder="음성 URL (풀 URL 권장)"
              />
            </div>
          </div>
          <div class="pg-line__image">
            <label class="p-field__label">Line Image</label>
            <image-upload
              :default-image="line.line_image || require('@/assets/img/no_image.png')"
              :unique-id="'line-image-' + index"
              v-model="line.line_image"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="pg-line__actions">
      <ABtn kind="yellow" icon="add" @click.prevent="addLine">Line 추가</ABtn>
      <ABtn kind="ghost" icon="upload_file" @click.prevent="triggerExcelUpload">Excel 일괄 등록</ABtn>
      <input
        ref="excelFileInput"
        type="file"
        accept=".xlsx, .xls"
        class="pg-line__file"
        @change="handleExcelUpload"
      />
    </div>
  </div>
</template>

<script setup>
import { defineModel, ref, watch } from "vue";
import ImageUpload from "@/components/ImageUpload.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import * as XLSX from "xlsx";

const sectionData = defineModel({ type: Object, required: true });
const excelFileInput = ref(null);

// 하위호환: 기존 line 콘텐츠에 lineMode 없으면 'plain' 보강
watch(
  () => sectionData.value.content,
  (content) => {
    if (content && !content.lineMode) content.lineMode = "plain";
  },
  { immediate: true }
);

const addLine = () => {
  sectionData.value.content.data.push({ left: "", right: "", line_image: "", audioUrl: "" });
};

const removeItem = (index) => {
  sectionData.value.content.data.splice(index, 1);
};

const moveUp = (index) => {
  if (index === 0) return;
  const data = sectionData.value.content.data;
  const temp = data[index];
  data[index] = data[index - 1];
  data[index - 1] = temp;
};

const moveDown = (index) => {
  const data = sectionData.value.content.data;
  if (index === data.length - 1) return;
  const temp = data[index];
  data[index] = data[index + 1];
  data[index + 1] = temp;
};

const triggerExcelUpload = () => {
  excelFileInput.value.click();
};

const handleExcelUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;
  try {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    let addedCount = 0;
    jsonData.forEach((row) => {
      if (!row || row.length === 0) return;
      const leftText = row[0] ? String(row[0]).trim() : "";
      const rightText = row[1] ? String(row[1]).trim() : "";
      if (leftText || rightText) {
        sectionData.value.content.data.push({
          left: leftText,
          right: rightText,
          line_image: "",
          audioUrl: "",
        });
        addedCount++;
      }
    });
    alert(`${addedCount}개의 항목을 추가했습니다.`);
    event.target.value = "";
  } catch (error) {
    console.error("Excel 파일 처리 오류:", error);
    alert("Excel 파일을 읽는 중 오류가 발생했습니다. 파일 형식을 확인해주세요.");
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.pg-line {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    background: $p-panel;
    border: 1px solid $p-line;
    border-radius: $p-radius-md;
    padding: 14px 16px;
    transition: border-color $p-dur $p-ease;

    &:hover { border-color: $p-line-hard; }
  }

  &__item-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    padding-bottom: 10px;
    border-bottom: 1px dashed $p-line;
  }

  &__index {
    min-width: 28px;
    height: 28px;
    padding: 0 10px;
    border-radius: 999px;
    background: $p-ink;
    color: $p-point;
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 800;
    display: inline-grid;
    place-items: center;
  }

  &__item-actions {
    display: flex;
    gap: 4px;
  }

  &__icon-btn {
    width: 30px;
    height: 30px;
    border: 0;
    background: transparent;
    color: $p-ink-mid;
    border-radius: $p-radius-sm;
    display: grid;
    place-items: center;
    cursor: pointer;
    transition: background $p-dur $p-ease, color $p-dur $p-ease;

    > i { font-size: 16px; }

    &:hover:not(:disabled) {
      background: $p-panel-alt;
      color: $p-ink;
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    &--danger {
      color: $p-err;

      &:hover:not(:disabled) {
        background: $p-err-soft;
        color: $p-err;
      }
    }
  }

  &__item-body {
    display: grid;
    grid-template-columns: 1fr 200px;
    gap: 16px;
    align-items: start;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &__fields {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__image {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__file {
    display: none;
  }

  &__audio-ico {
    font-size: 14px !important;
    color: $p-point-deep;
    vertical-align: middle;
    margin-right: 4px;
  }
}
</style>
